/**
 * TopicRegionPage — CMS-driven topic / region section page.
 *
 * Lookup strategy (both queries fire in parallel):
 *   1. Try live page: kind + slug  (e.g. kind=topic, slug=cybersecurity)
 *      → render as-is with its own pool config
 *   2. Try live page: kind + 'default'  (e.g. kind=topic, slug=default)
 *      → render with slug injected into pool at runtime
 *   3. Neither found → simple empty state
 *
 * The production desk creates one default topic layout and one default
 * region layout in the CMS. Any topic/region URL without a dedicated page
 * inherits the default layout; the pool's topicSlug / regionSlug is
 * overridden with the URL slug before articles are fetched.
 */

import { useMemo, useState, useTransition } from 'react'
import { Loader2 } from 'lucide-react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { usePagePoolArticles } from '../api/hooks/useArticles'
import { useLivePageByKindSlug } from '../api/hooks/usePages'
import { useTopics, useRegions, topicLabel, regionLabel } from '../hooks/useTaxonomy'
import { PageSlotRenderer, usePublishedSlotArticles } from '../components/page-renderer/PageSlotRenderer'
import { PageMetricsToolbar } from '../components/page-renderer/PageMetricsToolbar'
import { PageMetricsSunburst } from '../components/page-renderer/PageMetricsSunburst'
import { usePageInlineMetrics } from '../components/page-renderer/usePageInlineMetrics'
import PageLoader from '../components/common/PageLoader'
import type { CanvasSlot } from '../components/editor-shared/canvasTypes'
import type { PageBlock, BlockContent } from '../api/types'
import { useAuthz } from '../auth/useAuthz'

// ─── Block conversion helpers (same pattern as FrontPage) ────────────────────

function pageBlocksToSlots(blocks: PageBlock[]): CanvasSlot[] {
  return blocks
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((b) => ({
      id: b.id,
      type: b.type,
      order: b.order,
      config: b.config,
      columnBlocks: b.columnContent
        ? Object.fromEntries(
            Object.entries(b.columnContent).map(([k, colBlocks]) => [
              k,
              (colBlocks ?? []).map((cb) => ({ id: cb.id, type: cb.type })),
            ]),
          )
        : undefined,
    }))
}

function pageBlocksToContentMap(blocks: PageBlock[]): Record<string, BlockContent> {
  const map: Record<string, BlockContent> = {}
  for (const b of blocks) {
    if (b.content) map[b.id] = b.content
    if (b.columnContent) {
      for (const [colKey, colBlocks] of Object.entries(b.columnContent)) {
        for (const cb of colBlocks ?? []) {
          if (cb.content) map[`${b.id}:${colKey}:${cb.id}`] = cb.content
        }
      }
    }
  }
  return map
}

// ─── CMS page renderer ───────────────────────────────────────────────────────

function CmsPageView({
  blocks,
  pool,
  label,
  kind,
  navigate,
  slugOverride,
  pageId,
  adminMode,
  canToggleAdminMode,
  onToggleAdminMode,
}: {
  blocks: PageBlock[]
  pool: import('../api/types').PagePool | undefined
  label: string
  kind: 'topic' | 'region'
  navigate: ReturnType<typeof useNavigate>
  slugOverride?: string
  pageId?: string
  adminMode: boolean
  canToggleAdminMode: boolean
  onToggleAdminMode?: () => void
}) {
  const [hoverMetricsTarget, setHoverMetricsTarget] = useState<{ blockId: string | null; articleId: string | null } | null>(null)
  const [lockedMetricsTarget, setLockedMetricsTarget] = useState<{ blockId: string | null; articleId: string | null } | null>(null)
  // When rendering from the default page, inject the current URL slug into
  // the pool so articles are scoped to the right topic / region.
  const effectivePool = slugOverride
    ? {
        ...(pool ?? {}),
        ...(kind === 'topic' ? { topicSlug: slugOverride } : { regionSlug: slugOverride }),
      }
    : pool
  const slots = pageBlocksToSlots(blocks)
  const contentMap = pageBlocksToContentMap(blocks)
  const { articles: poolArticles, isPending: isPoolPending } = usePagePoolArticles(effectivePool)
  const { slotArticleMap, isPending: isSlotPending } = usePublishedSlotArticles(slots, contentMap, effectivePool ? poolArticles : undefined)
  const displayedArticleIds = useMemo(
    () => Array.from(new Set(Object.values(slotArticleMap).flat().map(article => article.id))),
    [slotArticleMap],
  )
  const { metricsByArticleId: pageMetricsByArticleId, isLoading: pageMetricsLoading } = usePageInlineMetrics(adminMode, pageId, displayedArticleIds)
  const totalPageViewsFromPage = useMemo(
    () => Object.values(pageMetricsByArticleId).reduce((sum, metrics) => sum + metrics.viewsFromPage, 0),
    [pageMetricsByArticleId],
  )
  const activeMetricsTarget = lockedMetricsTarget ?? hoverMetricsTarget

  if (isPoolPending || isSlotPending) return <PageLoader />

  return (
    <div className="mx-auto max-w-7xl px-6 pb-12 pt-6">
      <div className="mb-8 border-b border-line pb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">
              {kind === 'topic' ? 'Topic' : 'Region'}
            </div>
            <h1 className="mt-1 font-zodiak text-4xl font-bold text-fg">{label}</h1>
          </div>
          <PageMetricsToolbar
            adminMode={adminMode}
            canToggleAdminMode={canToggleAdminMode}
            onToggleAdminMode={onToggleAdminMode}
          />
        </div>
      </div>
      {adminMode && pageMetricsLoading && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg-secondary">
          <Loader2 size={14} className="animate-spin" aria-hidden="true" />
          Loading inline page metrics...
        </div>
      )}
      {adminMode && totalPageViewsFromPage > 0 && (
        <div className="mb-8">
          <PageMetricsSunburst
            slots={slots}
            slotArticleMap={slotArticleMap}
            pageMetricsByArticleId={pageMetricsByArticleId}
            totalPageViewsFromPage={totalPageViewsFromPage}
            lockedTarget={lockedMetricsTarget}
            onHoverChange={target => {
              if (!lockedMetricsTarget) setHoverMetricsTarget(target)
            }}
            onLockToggle={target => {
              setLockedMetricsTarget(current => {
                if (current && current.blockId === target.blockId && current.articleId === target.articleId) {
                  return null
                }
                return target
              })
            }}
          />
        </div>
      )}
      <div className="space-y-10">
        {Object.values(slotArticleMap).every((a) => a.length === 0) ? (
          <div className="flex h-48 items-center justify-center text-sm text-fg-secondary">
            No content available.
          </div>
        ) : (
          slots.map((slot) => (
            <PageSlotRenderer
              key={slot.id}
              slot={slot}
              articles={slotArticleMap[slot.id] ?? []}
              slotArticleMap={slotArticleMap}
              showPageMetrics={adminMode && !pageMetricsLoading}
              pageMetricsByArticleId={pageMetricsByArticleId}
              totalPageViewsFromPage={totalPageViewsFromPage}
              activeBlockId={activeMetricsTarget?.blockId ?? null}
              activeArticleId={activeMetricsTarget?.articleId ?? null}
              onArticleClick={(article) => navigate(`/wire/articles/${article.id}`, { state: { navMethod: 'page', pageId } })}
            />
          ))
        )}
      </div>
    </div>
  )
}

// ─── Page component ───────────────────────────────────────────────────────────

interface TopicRegionPageProps {
  kind: 'topic' | 'region'
}

export default function TopicRegionPage({ kind }: TopicRegionPageProps) {
  const { slug = '' } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { isWorkflowMember } = useAuthz()
  const [adminMode, setAdminMode] = useState(false)
  const [, startTransition] = useTransition()

  const { data: topics = [] } = useTopics()
  const { data: regions = [] } = useRegions()

  const label =
    kind === 'topic' ? topicLabel(topics, slug) : regionLabel(regions, slug)

  // Both queries fire in parallel — specific page takes priority over default
  const { data: specificPage, isPending: isSpecificPending, isError: isSpecificError } = useLivePageByKindSlug(kind, slug)
  const { data: defaultPage, isPending: isDefaultPending } = useLivePageByKindSlug(kind, 'default')

  // A CMS page is only usable if it has a pool config and at least one pool-sourced block.
  // Pages without pool blocks were created before pool support and can't show articles
  // in the reader context (static IDs require the admin drafts endpoint).
  const isPoolReady = (p: import('../api/types').Page | undefined) =>
    !!p?.pool && p.blocks.length > 0

  // Always fetch articles for this slug — used as fallback when no CMS page exists
  const fallbackPool = kind === 'topic' ? { topicSlug: slug } : { regionSlug: slug }
  const { articles: fallbackArticles, isPending: isFallbackPending } = usePagePoolArticles(fallbackPool)

  // Wait for the specific page lookup
  if (isSpecificPending) return <PageLoader />

  // Specific page found with pool-capable blocks — render as-is with its own pool
  if (!isSpecificError && isPoolReady(specificPage)) {
    return (
      <CmsPageView
        blocks={specificPage!.blocks}
        pool={specificPage!.pool}
        label={label}
        kind={kind}
        navigate={navigate}
        pageId={specificPage!.id}
        adminMode={adminMode}
        canToggleAdminMode={isWorkflowMember}
        onToggleAdminMode={isWorkflowMember ? () => startTransition(() => setAdminMode(current => !current)) : undefined}
      />
    )
  }

  // No specific page — wait for default if still in-flight
  if (isDefaultPending) return <PageLoader />

  // Default page found with pool-capable blocks — inject current URL slug into pool at runtime
  if (isPoolReady(defaultPage)) {
    return (
      <CmsPageView
        blocks={defaultPage!.blocks}
        pool={defaultPage!.pool}
        label={label}
        kind={kind}
        navigate={navigate}
        slugOverride={slug}
        pageId={defaultPage!.id}
        adminMode={adminMode}
        canToggleAdminMode={isWorkflowMember}
        onToggleAdminMode={isWorkflowMember ? () => startTransition(() => setAdminMode(current => !current)) : undefined}
      />
    )
  }

  // Neither CMS page exists — fall back to a plain article list for this slug
  if (isFallbackPending) return <PageLoader />

  return (
    <div className="mx-auto max-w-7xl px-6 pb-12 pt-6">
      <div className="mb-8 border-b border-line pb-5">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">
          {kind === 'topic' ? 'Topic' : 'Region'}
        </div>
        <h1 className="mt-1 font-zodiak text-4xl font-bold text-fg">{label}</h1>
      </div>
      {fallbackArticles.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-sm text-fg-secondary">
          No content available for this {kind}.
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {fallbackArticles.map((article) => {
            const href = `/wire/articles/${article.articleId ?? article.id}`
            const date = article.publishedAt ?? article.updatedAt
            const dateStr = date
              ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
              : null
            return (
              <li key={article.id}>
                <Link to={href} className="block py-4 hover:bg-fill transition-colors px-2 -mx-2 rounded-sm">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="font-semibold text-fg">{article.title}</span>
                    {dateStr && <span className="shrink-0 text-[11px] text-fg-secondary">{dateStr}</span>}
                  </div>
                  {(article.subtitle ?? article.description) && (
                    <p className="mt-0.5 text-sm text-fg-secondary line-clamp-2">
                      {article.subtitle ?? article.description}
                    </p>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
